// src/components/transfers/ShipBatchDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Truck } from 'lucide-react';
import { productApi } from '../../api';

// 到期状态：过期/临期（7天内）/正常——和 InventoryManagementPage.jsx 用的是同一套判断逻辑。
// <option> 标签浏览器对样式支持很有限，没法直接标红，用文字前缀代替，
// 再在下面单独一行提示当前选中批次的状态，那一行是普通文字，样式可以正常控制
function getExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 7) return 'soon';
  return 'ok';
}
function batchOptionLabel(b, item) {
  const status = getExpiryStatus(b.expiry_date);
  const prefix = status === 'expired' ? '⚠️已过期 · ' : status === 'soon' ? '⏰临期 · ' : '';
  const insufficientSuffix = b.quantity < item.quantity ? ' — 库存不够' : '';
  return `${prefix}${b.batch_no}（剩余 ${b.quantity} 个${b.expiry_date ? `，到期 ${b.expiry_date}` : ''}）${insufficientSuffix}`;
}

// 确认发货前，给这张划拨单里的每一件商品，选一个具体从哪个批次发——
// 之前点"确认发货"是直接扣 products.stock 这个笼统的数字，现在批次系统上线后，
// 必须精确指定发的是哪一批，这一步不能再跳过
const ShipBatchDialog = ({ transferNo, items, onClose, onShip }) => {
  // batchOptions: { [transfer_item_id]: batch[] }
  const [batchOptions, setBatchOptions] = useState({});
  const [selectedBatch, setSelectedBatch] = useState({}); // { [transfer_item_id]: batch_id }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadAllBatches = async () => {
      setLoading(true);
      const result = {};
      const defaults = {};
      for (const item of items) {
        try {
          const res = await productApi.getAvailableBatches(item.product_id);
          const batches = res.success ? (res.data || []) : [];
          result[item.id] = batches;
          // 默认选第一个够数量的批次（列表本身已经按到期日期从早到晚排好了）
          const enough = batches.find(b => b.quantity >= item.quantity);
          if (enough) defaults[item.id] = enough.id;
        } catch (err) {
          console.error(`加载「${item.product_name}」批次失败:`, err);
          result[item.id] = [];
        }
      }
      setBatchOptions(result);
      setSelectedBatch(defaults);
      setLoading(false);
    };
    loadAllBatches();
  }, [items]);

  const allSelected = items.every(item => selectedBatch[item.id]);

  // 每件商品，选中的那个批次库存够不够这次要发的数量
  const getInsufficientItems = () => {
    return items.filter(item => {
      const batchId = selectedBatch[item.id];
      if (!batchId) return false;
      const batch = (batchOptions[item.id] || []).find(b => b.id === parseInt(batchId));
      return batch && batch.quantity < item.quantity;
    });
  };
  const insufficientItems = getInsufficientItems();

  const handleConfirm = async () => {
    if (!allSelected) { alert('请为每件商品选择发货批次'); return; }
    if (insufficientItems.length > 0) {
      alert(`「${insufficientItems[0].product_name}」所选批次库存不足，请换一个批次或先去编辑这一行拆分数量`);
      return;
    }
    setSubmitting(true);
    try {
      const batch_assignments = items.map(item => ({
        transfer_id: item.id,
        batch_id: parseInt(selectedBatch[item.id]),
      }));
      await onShip(transferNo, batch_assignments);
    } catch (err) {
      alert(err.message || '发货失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="border-b p-6 flex justify-between items-center flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold">确认发货</h3>
            <p className="text-sm text-gray-500 mt-0.5">{transferNo} · 为每件商品选择发货批次</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-400">加载批次信息中...</div>
          ) : (
            items.map(item => {
              const batches = batchOptions[item.id] || [];
              const noBatches = batches.length === 0;
              return (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-800">{item.product_name}</span>
                    <span className="text-sm text-gray-500">需要 {item.quantity} 个</span>
                  </div>
                  {noBatches ? (
                    <p className="text-sm text-red-500">⚠️ 该商品当前没有可用批次，无法发货</p>
                  ) : (
                    <>
                      <select
                        value={selectedBatch[item.id] || ''}
                        onChange={e => setSelectedBatch(prev => ({ ...prev, [item.id]: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">请选择批次</option>
                        {batches.map(b => (
                          <option key={b.id} value={b.id} disabled={b.quantity < item.quantity}>
                            {batchOptionLabel(b, item)}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const selected = batches.find(b => String(b.id) === String(selectedBatch[item.id]));
                        if (!selected) return null;
                        const status = getExpiryStatus(selected.expiry_date);
                        if (status === 'expired') return <p className="text-xs text-red-500 mt-1">⚠️ 所选批次已过期，请确认是否仍要发货</p>;
                        if (status === 'soon') return <p className="text-xs text-amber-600 mt-1">⏰ 所选批次7天内到期</p>;
                        return null;
                      })()}
                    </>
                  )}
                </div>
              );
            })
          )}

          {!loading && insufficientItems.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                ⚠️ 有商品单个批次库存不够这次要发的数量——如果需要跨批次拼单发货，
                请先关闭这个弹窗，用"编辑"把对应商品拆成多行（各自选不同批次的量），再来确认发货
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || submitting || !allSelected}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Truck className="w-4 h-4" />
            {submitting ? '发货中...' : '确认发货'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShipBatchDialog;